import { Alert, Box, Text, useColor } from 'braid-design-system';
import type { Chart } from 'chart.js';
import { format } from 'date-fns/format';
import { isSameDay } from 'date-fns/isSameDay';
import { enAU } from 'date-fns/locale';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from 'react';
import type { Line } from 'react-chartjs-2';

import { randomID } from '../utilities.ts';

import {
  GraphTooltip,
  useGraphTooltipFloating,
  type TooltipData,
} from './GraphTooltip.tsx';
import { LineChartWithInteractiveLegend } from './LineChartWithInteractiveLegend.tsx';
import { mapFilterLabelToFilterValue, type GroupBy } from './config.ts';
import { externalTooltip } from './externalTooltip.ts';
import { shouldConsiderAsLoading } from './loading.ts';
import type { FilterData } from './types.ts';
import { useIntegrationHealthGraphData } from './useIntegrationHealthGraphData.tsx';

import { useLazyColorMode } from '#src/components/ColorModeProvider/ColorModeProvider.tsx';
import {
  BlockLoadingPlaceholder,
  LoadingOverlay,
} from '#src/components/LoadingPlaceholder/LoadingPlaceholder.tsx';
import { useWindowWidth } from '#src/hooks/useWindowWidth.ts';

const chooseTickCount = (windowWidth: number) => {
  if (windowWidth < 740) {
    return 3;
  }

  if (windowWidth < 1200) {
    return 5;
  }

  return 8;
};

const useChartOptions = ({
  onChangeBounds,
  bounds,
  floating,
  setTooltipData,
  parentRef,
}: {
  onChangeBounds: (bounds: [Date, Date]) => void;
  bounds: [Date, Date];
  floating: ReturnType<typeof useGraphTooltipFloating>;
  setTooltipData: React.Dispatch<React.SetStateAction<TooltipData | null>>;
  parentRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const color = useColor();
  const lazyColorMode = useLazyColorMode();
  const windowWindow = useWindowWidth();

  const gridColor =
    lazyColorMode?.colorMode === 'darkMode'
      ? color.background.neutral
      : color.background.neutralLight;

  const textColor =
    lazyColorMode?.colorMode === 'darkMode'
      ? color.foreground.secondaryInverted
      : color.foreground.secondary;

  const onZoomComplete = useCallback(
    ({ chart }: { chart: Chart }) => {
      onChangeBounds([
        new Date(chart.scales.x.min),
        new Date(chart.scales.x.max),
      ]);
    },
    [onChangeBounds],
  );

  return useMemo(
    (): ComponentProps<typeof Line>['options'] => ({
      maintainAspectRatio: false,
      responsive: true,
      spanGaps: false,

      elements: {
        line: {
          // Smooth out sharp corners
          tension: 0.01,
          borderWidth: 2,
        },
      },

      interaction: {
        intersect: false,
        mode: 'nearest',
        axis: 'x',
      },

      scales: {
        x: {
          type: 'time',
          min: bounds[0].getTime(),
          max: bounds[1].getTime(),
          grid: { color: gridColor },
          ticks: {
            color: textColor,
            includeBounds: false,
            maxRotation: 0,
            autoSkipPadding: 10,
            autoSkip: true,
            maxTicksLimit: chooseTickCount(windowWindow),
          },
          time: {
            tooltipFormat: 'EEE do MMM, HH:mm:ss',
          },
          beforeFit: (scale) => {
            if (scale.ticks.length < 2) {
              return;
            }

            if (
              !['millisecond', 'second', 'minute', 'hour'].includes(
                // @ts-expect-error - peeping on internals :(
                // values from https://github.com/chartjs/chartjs-adapter-date-fns/blob/fa17f9dd1f5a275fcaf44dfb1e73f7bd79458de3/src/index.js#L15-L26
                scale._unit,
              )
            ) {
              return;
            }

            const addToAll = !isSameDay(
              scale.ticks.at(-1)!.value,
              scale.ticks[0].value,
            );

            scale.ticks.forEach((tick, index) => {
              const tickLabelString = Array.isArray(tick.label)
                ? tick.label.join(' ')
                : (tick.label ?? '');

              if (index === 0 || addToAll) {
                tick.label = [
                  tickLabelString,
                  format(tick.value, 'EEE do MMM'),
                ];
              }
            });
          },
          adapters: {
            date: {
              locale: enAU,
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: gridColor },
          ticks: { color: textColor },
        },
      },

      plugins: {
        legend: {
          display: false,
          labels: { color: textColor },
        },
        tooltip: {
          enabled: false,
          external: externalTooltip({
            setTooltipData,
            floatingRefs: floating.refs,
            parentRef,
          }),
        },
        zoom: {
          zoom: {
            drag: {
              enabled: true,
            },
            mode: 'x',
            onZoomComplete,
          },
        },
      },
    }),
    [
      onZoomComplete,
      bounds,
      setTooltipData,
      floating.refs,
      parentRef,
      gridColor,
      textColor,
      windowWindow,
    ],
  );
};

export const IntegrationHealthRequestsGraph = ({
  onChangeBounds,
  bounds,
  filters,
  skipLoading,
  onAddFilter,
  groupBy,
  setGroupBy,
}: {
  onChangeBounds: (bounds: [Date, Date]) => void;
  onAddFilter: (filter: FilterData) => void;
  bounds: [Date, Date];
  filters: FilterData[];
  skipLoading?: boolean;
  groupBy: GroupBy;
  setGroupBy: (groupBy: GroupBy) => void;
}) => {
  const { query, data } = useIntegrationHealthGraphData({
    bounds,
    filters,
    skipLoading,
    groupBy,
  });

  const parentRef = useRef<HTMLDivElement>(null);
  const floating = useGraphTooltipFloating();

  const [tooltipData, setTooltipData] = useState<null | TooltipData>(null);

  const options = useChartOptions({
    onChangeBounds,
    setTooltipData,
    bounds:
      // Prefer the effective bounds corresponding to what data we've rendered, if available
      query.status === 'success' && query.data?.parameters.timeRange
        ? [
            new Date(query.data.parameters.timeRange.start),
            new Date(query.data.parameters.timeRange.end),
          ]
        : bounds,
    floating,
    parentRef,
  });

  const isLoading = useMemo(
    () =>
      query.fetchStatus === 'fetching' &&
      shouldConsiderAsLoading({ query, filters, groupBy, bounds }),
    [query, filters, groupBy, bounds],
  );

  if (query.status === 'error') {
    return (
      <Alert tone="critical">
        <Text>Failed to load integration requests data.</Text>
      </Alert>
    );
  }

  if (!data) {
    return (
      <BlockLoadingPlaceholder>
        <LineChartWithInteractiveLegend
          options={options}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          data={
            data ?? {
              labels: [],
              datasets: [],
            }
          }
          parentRef={parentRef}
        />
      </BlockLoadingPlaceholder>
    );
  }

  const showFilters =
    groupBy &&
    !filters.some((f) => f.type === groupBy && f.comparator === 'Equal') &&
    groupBy !== 'None';

  return (
    <Box position="relative">
      {isLoading ? <LoadingOverlay /> : null}

      <LineChartWithInteractiveLegend
        options={options}
        data={data}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        onExcludeSeries={
          showFilters
            ? (label) =>
                onAddFilter({
                  id: randomID(),
                  type: groupBy,
                  comparator: 'NotEqual',
                  value: mapFilterLabelToFilterValue(label, groupBy),
                  label,
                })
            : undefined
        }
        onFocusSeries={
          showFilters
            ? (label) =>
                onAddFilter({
                  id: randomID(),
                  type: groupBy,
                  comparator: 'Equal',
                  value: mapFilterLabelToFilterValue(label, groupBy),
                  label,
                })
            : undefined
        }
        parentRef={parentRef}
      />

      <GraphTooltip tooltipData={tooltipData} floating={floating} />
    </Box>
  );
};
